import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { visualInsetRatiosForShape } from '@/lib/shapes/shape-visual-bounds'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import {
  type SmartGuideLine,
  computeSmartGuides,
  extractLayoutSnapLines,
} from '@/lib/guides/smart-guides'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import type { UnifiedSelectionResizeSession } from '@/lib/transforms/unified-selection-resize'
import { computeSingleObjectOrientedResize } from '@/lib/transforms/single-object-oriented-resize'
import {
  getMaxPhotoFillSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import {
  SHAPE_MIN_DIMENSION,
  TEXT_MIN_DIMENSION,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import {
  type MultiSelectResizePhotoConstraint,
  type MultiSelectResizeShapeSnap,
  applyMultiResizeCoverageCorrection,
  canvasDeltaToMultiResizeLocal,
  clampMultiSelectResizeToPhotoResolution,
  computeMultiSelectResize,
} from '@/lib/transforms/multi-select-chrome-resize'
import type { InteractionState, ResizeHandle } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

export type UpdateShape = ShapeManagerRef['updateShape']

/** Pointer delta (canvas units, from gesture start) and modifier for one resize step. */
export interface ResizePointer {
  dx: number
  dy: number
  shiftKey: boolean
}

/**
 * Resize floor for one shape. A text frame keeps the dedicated `TEXT_MIN_DIMENSION` (10px, matching
 * the text tool's own create minimum) rather than the generic `SHAPE_MIN_DIMENSION` (0.01px): a
 * shape may legitimately be resized to a hairline, but a text frame shrunk that far is a degenerate
 * object with no legible content that is then hard to reselect or drag back up.
 *
 * Text-unification regression: when text frames became `ShapeData`, they picked up the shape floor
 * and `TEXT_MIN_DIMENSION` was left referenced nowhere — the orphaned constant was the tell.
 */
export function minResizeDimensionForShape(shape: ShapeData): number {
  return isShapeTextFrame(shape) ? TEXT_MIN_DIMENSION : SHAPE_MIN_DIMENSION
}

/**
 * Photo-resolution ceilings for a multi-select resize, keyed by shape id.
 *
 * Takes `photoFillInitials` (frozen once at gesture start, on the resize session) rather than
 * reading `shapesRef.current` live — this function runs on every animation frame of the drag,
 * and by the second frame onward, live shape state already carries the *previous* frame's
 * clamped `offsetX/offsetY`. Feeding that into `computePhotoFillOffsetAfterResize`'s "before"
 * side (via `clampMultiSelectResizeToPhotoResolution`) makes the offset math history-dependent
 * instead of a clean function of (gesture-start rect, final rect) — a photo's pan can get
 * clamped and stay stuck on an intermediate frame with tighter crop slack, even though the
 * final release size would have allowed the original pan position back.
 */
export function buildPhotoConstraintsById(
  shapeInitials: Record<string, MultiSelectResizeShapeSnap>,
  photoFillInitials: Record<string, ShapePhotoFill | undefined>
): Record<string, MultiSelectResizePhotoConstraint | undefined> {
  const constraints: Record<string, MultiSelectResizePhotoConstraint | undefined> = {}

  for (const id of Object.keys(shapeInitials)) {
    const initialPhotoFill = photoFillInitials[id]

    if (initialPhotoFill) {
      const { naturalWidth: effectiveNaturalWidth, naturalHeight: effectiveNaturalHeight } =
        resolveEffectivePhotoFillNaturalSize(initialPhotoFill)
      const maxSize = getMaxPhotoFillSize(effectiveNaturalWidth, effectiveNaturalHeight)

      constraints[id] = {
        maxWidth: maxSize?.maxWidth,
        maxHeight: maxSize?.maxHeight,
        // Carries the fallback-resolved natural size through to
        // `clampMultiSelectResizeToPhotoResolution`, which reads `naturalWidth/Height`
        // straight off this `photoFill` for its own scale-freeze/slack-clamp math.
        photoFill: {
          ...initialPhotoFill,
          naturalWidth: effectiveNaturalWidth ?? undefined,
          naturalHeight: effectiveNaturalHeight ?? undefined,
        },
      }
    }
  }

  return constraints
}

export interface MultiSelectResizeStepDeps {
  panelDimensionsLinked: boolean
  updateShape: UpdateShape
  setMultiSelectChrome: Dispatch<SetStateAction<MultiSelectChrome | null>>
  multiSelectChromeRef: MutableRefObject<MultiSelectChrome | null>
}

/**
 * One step of a multi-select (unified session) resize — compute the oriented resize, clamp it to
 * each photo's resolution, apply it, and correct the chrome for coverage. The live frame and the
 * commit run exactly this sequence.
 */
export function applyMultiSelectResizeStep(
  resizeSession: UnifiedSelectionResizeSession,
  dx: number,
  dy: number,
  shiftKey: boolean,
  {
    panelDimensionsLinked,
    updateShape,
    setMultiSelectChrome,
    multiSelectChromeRef,
  }: MultiSelectResizeStepDeps
): void {
  const { x: localDx, y: localDy } = canvasDeltaToMultiResizeLocal(
    dx,
    dy,
    resizeSession.chromeStart.rotationDeg
  )
  const aspectLock = { current: resizeSession.aspectLockAxis }
  const rawResult = computeMultiSelectResize(
    resizeSession.chromeStart,
    resizeSession.localInitial,
    resizeSession.shapeInitials,
    resizeSession.direction,
    localDx,
    localDy,
    shiftKey,
    aspectLock,
    panelDimensionsLinked,
    Object.values(resizeSession.photoFillInitials).some(Boolean)
  )

  const { nextChrome, updates } = clampMultiSelectResizeToPhotoResolution(
    rawResult,
    buildPhotoConstraintsById(resizeSession.shapeInitials, resizeSession.photoFillInitials),
    resizeSession.chromeStart,
    resizeSession.direction,
    resizeSession.localInitial.width,
    resizeSession.localInitial.height,
    resizeSession.shapeInitials
  )

  resizeSession.aspectLockAxis = aspectLock.current

  for (const [id, u] of Object.entries(updates)) {
    updateShape(id, u)
  }

  const correctedChrome = applyMultiResizeCoverageCorrection(
    nextChrome,
    resizeSession.direction,
    resizeSession.shapeInitials,
    resizeSession.boundsInitials,
    updates
  )

  setMultiSelectChrome(correctedChrome)
  multiSelectChromeRef.current = correctedChrome
}

export interface SingleResizeDeps {
  panelDimensionsLinked: boolean
  smartGuidesEnabled: boolean
  getOtherShapeBounds: (excludeIds: Set<string>) => ObjectBounds[]
  effectiveSnapGuideDistance: number
  /** Ruler-guide snap lines, already gated on the snap-to-guides setting. */
  layoutSnapLines: ReturnType<typeof extractLayoutSnapLines> | undefined
  /** Live frames show the smart guides they snapped to; the commit does not. */
  onGuides?: (guides: SmartGuideLine[]) => void
}

/**
 * Single-object handle resize, snapped to smart guides when enabled. Updates
 * `current.aspectLockAxis` in place, as the gesture's aspect lock latches across frames.
 */
export function computeSingleObjectResize(
  current: InteractionState,
  direction: ResizeHandle,
  latest: ResizePointer,
  {
    panelDimensionsLinked,
    smartGuidesEnabled,
    getOtherShapeBounds,
    effectiveSnapGuideDistance,
    layoutSnapLines,
    onGuides,
  }: SingleResizeDeps
): {
  dir: ResizeHandle
  next: { x: number; y: number; width: number; height: number }
  useAspectRatio: boolean
  visualRatios: ReturnType<typeof visualInsetRatiosForShape>
} {
  const dir = direction
  let guideDx = latest.dx
  let guideDy = latest.dy
  const aspectLock = { current: current.aspectLockAxis }
  const hasPhotoFillForAspectLock =
    !!current.initial.photoFill ||
    !!(current.groupPhotoFillInitials && Object.keys(current.groupPhotoFillInitials).length > 0)
  // The resize handle is drawn on the shape's visual box, so the drag has to be applied
  // there and converted back to the geometric rect — otherwise an inset shape (triangle,
  // star) trails the pointer by its own inset ratio. Scale-invariant, so hoisting this
  // out of the per-frame path isn't necessary; `current.initial` is frozen for the gesture.
  const visualRatios = visualInsetRatiosForShape(current.initial)
  const computed = computeSingleObjectOrientedResize(
    current.initial,
    dir,
    guideDx,
    guideDy,
    latest.shiftKey,
    aspectLock,
    panelDimensionsLinked,
    minResizeDimensionForShape(current.initial),
    hasPhotoFillForAspectLock,
    visualRatios
  )
  let { next, useAspectRatio } = computed

  current.aspectLockAxis = aspectLock.current

  if (smartGuidesEnabled) {
    const otherBounds = getOtherShapeBounds(new Set([current.shapeId]))
    const resizedBounds: ObjectBounds = {
      x: next.x,
      y: next.y,
      width: next.width,
      height: next.height,
    }
    const sgResult = computeSmartGuides(
      resizedBounds,
      otherBounds,
      effectiveSnapGuideDistance,
      layoutSnapLines
    )

    onGuides?.(sgResult.guides)
    const adjX = sgResult.snapAdjust.dx
    const adjY = sgResult.snapAdjust.dy

    if ((dir.includes('e') || dir.includes('w')) && adjX !== 0) {
      guideDx = dir.includes('e') ? guideDx + adjX : guideDx - adjX
    }

    if ((dir.includes('s') || dir.includes('n')) && adjY !== 0) {
      guideDy = dir.includes('s') ? guideDy + adjY : guideDy - adjY
    }

    if (guideDx !== latest.dx || guideDy !== latest.dy) {
      const snappedAspectLock = { current: current.aspectLockAxis }
      const snapped = computeSingleObjectOrientedResize(
        current.initial,
        dir,
        guideDx,
        guideDy,
        latest.shiftKey,
        snappedAspectLock,
        panelDimensionsLinked,
        minResizeDimensionForShape(current.initial),
        hasPhotoFillForAspectLock,
        visualRatios
      )

      ;({ next, useAspectRatio } = snapped)
      current.aspectLockAxis = snappedAspectLock.current
    }
  }

  return { dir, next, useAspectRatio, visualRatios }
}
